/**
 * The platform (manage-v2) cohort. Today exactly one org — "Mid July Testing",
 * the same org already allow-listed on the Fargate task's PROOF_ALLOWED_USER_IDS
 * — gets the platform-backed entry flow: the Home composer opens a `/session/…`
 * that creates a real ATS Job through the platform gateway and lands in
 * manage-v2, instead of the legacy agent-worker session.
 *
 * Deliberately an id list rather than a temporaryPreset: this is a rollout
 * cohort, not a purchased feature, and it must not be settable by an org admin.
 */
const PLATFORM_COHORT_ORGANIZATION_IDS: readonly string[] = ['6a5e887315893cff64f4663f'];

export function isPlatformCohortOrg(
    organization: { _id?: string } | null | undefined
): boolean {
    const id = organization?._id;
    return typeof id === 'string' && PLATFORM_COHORT_ORGANIZATION_IDS.includes(id);
}

export const SEARCH_ONLY_TEMPORARY_PRESET = 'search-only' as const;
export const OUTREACH_TEMPORARY_PRESET = 'outreach' as const;
export const CRYPTO_LOCAL_SEARCH_TEMPORARY_PRESET = 'crypto-local-search' as const;
/**
 * Upgrades ONE org to the manage-v2 experience without a deploy (owner
 * decision 2026-07-29). Unlike the other presets this is a rollout gate, not
 * a purchased feature — which is why `isDefaultOrg` below deliberately
 * ignores it: flipping an org to v2 must not also change its Home nav,
 * search toggle, or attachment behaviour.
 */
export const MANAGE_V2_TEMPORARY_PRESET = 'manage-v2' as const;

export type TemporaryPresetFeature =
    | typeof SEARCH_ONLY_TEMPORARY_PRESET
    | typeof OUTREACH_TEMPORARY_PRESET
    | typeof CRYPTO_LOCAL_SEARCH_TEMPORARY_PRESET
    | typeof MANAGE_V2_TEMPORARY_PRESET;

export type TemporaryPresetValue = string | string[] | null;

type OrganizationWithPreset = {
    temporaryPreset?: TemporaryPresetValue;
};

export function parseTemporaryPresetFeatures(value: TemporaryPresetValue | undefined): string[] {
    if (!value) return [];
    const rawValues = Array.isArray(value) ? value : value.split(/[\s,;|]+/);
    return [...new Set(rawValues.map((feature) => feature.trim()).filter(Boolean))];
}

export function hasTemporaryPresetFeature(
    organization: OrganizationWithPreset | null | undefined,
    feature: TemporaryPresetFeature
): boolean {
    return parseTemporaryPresetFeatures(organization?.temporaryPreset).includes(feature);
}

export function isSearchOnlyOrg(organization: OrganizationWithPreset | null | undefined): boolean {
    return hasTemporaryPresetFeature(organization, SEARCH_ONLY_TEMPORARY_PRESET);
}

export function hasOutreachPreset(organization: OrganizationWithPreset | null | undefined): boolean {
    return hasTemporaryPresetFeature(organization, OUTREACH_TEMPORARY_PRESET);
}

export function hasCryptoLocalSearch(organization: OrganizationWithPreset | null | undefined): boolean {
    return hasTemporaryPresetFeature(organization, CRYPTO_LOCAL_SEARCH_TEMPORARY_PRESET);
}

/**
 * True when the org carries NO temporary-preset features at all — the
 * "default settings" org. The default experience is deliberately pared
 * back (minimal 3-item nav, no search toggle on Home, attachments always
 * on) and must NOT change behavior for any preset-flagged org (search-only,
 * outreach, crypto-local-search, …). Gate the default-only surfaces on this.
 */
export function isDefaultOrg(organization: OrganizationWithPreset | null | undefined): boolean {
    return (
        parseTemporaryPresetFeatures(organization?.temporaryPreset)
            // manage-v2 is a rollout gate, not an experience preset. Counting
            // it here would silently strip the default Home experience from
            // any org upgraded to v2.
            .filter((feature) => feature !== MANAGE_V2_TEMPORARY_PRESET).length === 0
    );
}

/**
 * True when this org gets the manage-v2 experience: the Home composer opens a
 * platform `/session/…` that creates a real ATS Job through the platform
 * gateway and lands in manage-v2, instead of the legacy agent-worker session.
 *
 * Two sources, deliberately: the hardcoded rollout cohort above (which cannot
 * be self-enabled) OR the `manage-v2` temporary preset (which an org admin
 * can set, and which is how a new org is upgraded without a deploy). Every
 * call site uses THIS, never `isPlatformCohortOrg` directly.
 */
export function isManageV2Org(
    organization: ({ _id?: string } & OrganizationWithPreset) | null | undefined
): boolean {
    return (
        isPlatformCohortOrg(organization) ||
        hasTemporaryPresetFeature(organization, MANAGE_V2_TEMPORARY_PRESET)
    );
}

export function serializeTemporaryPresetFeatures(features: string[]): TemporaryPresetValue {
    const normalized = [...new Set(features.map((feature) => feature.trim()).filter(Boolean))];
    if (normalized.length === 0) return null;
    return normalized.length === 1 ? normalized[0] : normalized;
}
